import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Box, Typography, TextField, MenuItem, Button, IconButton, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DropdownTextField from '../form-components/DropdownTextField';
import LocalText from '../common/LocalText';
import { textFieldSx } from '../../utils/dropdownStyles';
import { getSports, getPersonalityById, createPersonality, updatePersonality } from '../../api/personalityApi';
import { extractErrorMessage } from '../../utils/errorMessage';

const emptyBio = () => [{ localeId: 'en_us', value: '' }];
const emptyPhoto = () => [{ localeId: 'en_us', mediaLink: '' }];

const PersonalityFormModal = ({ open, onClose, onSaved, personalityId, entityId, spaceId }) => {
  const isEdit = Boolean(personalityId);

  const [name, setName]       = useState('');
  const [sportId, setSportId] = useState('');
  const [bio, setBio]         = useState(emptyBio());
  const [photo, setPhoto]     = useState(emptyPhoto());
  const [sports, setSports]   = useState([]);
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const resetFromServer = (data) => {
    setName(data?.name ?? '');
    setSportId(data?.sportId ?? '');
    setBio(
      data?.localizedBio?.values?.length > 0
        ? data.localizedBio.values.map((v) => ({ localeId: v.localeId, value: v.value }))
        : emptyBio()
    );
    setPhoto(
      data?.media?.linkLocalizations?.length > 0
        ? data.media.linkLocalizations.map((v) => ({ localeId: v.localeId, mediaLink: v.mediaLink }))
        : emptyPhoto()
    );
  };

  useEffect(() => {
    if (!open) return;
    getSports().then(setSports).catch(() => setSports([]));
    setError('');
    if (isEdit) {
      setLoading(true);
      getPersonalityById(personalityId)
        .then(resetFromServer)
        .catch(() => setError('Could not load this personality.'))
        .finally(() => setLoading(false));
    } else {
      setName('');
      setSportId('');
      setBio(emptyBio());
      setPhoto(emptyPhoto());
    }
  }, [open, isEdit, personalityId]);

  const isValid = name.trim() !== '';

  const handleSubmit = async () => {
    if (!isValid) return;
    setSaving(true);
    setError('');
    const bioFilled   = bio.some((v) => v.value.trim());
    const photoFilled = photo.some((v) => (v.mediaLink ?? '').trim());
    const payload = {
      name: name.trim(),
      sportId: sportId === '' ? null : Number(sportId),
      entityId,
      spaceId,
      localizedCountry: { key: null, values: [] },
      localizedBio: { key: null, values: bioFilled ? bio : [] },
      media: photoFilled ? {
        mediaTypeId: 1,
        linkLocalizations: photo.filter((v) => (v.mediaLink ?? '').trim()),
      } : null,
    };
    try {
      const saved = isEdit
        ? await updatePersonality(personalityId, payload)
        : await createPersonality(payload);
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not save this personality.'));
      if (isEdit) {
        // Blocked (or failed) — reset to server state rather than leaving a half-applied edit.
        getPersonalityById(personalityId).then(resetFromServer).catch(() => {});
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <Box sx={{ bgcolor: '#0a326d', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 3 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, fontSize: 16 }}>
          <LocalText>{isEdit ? 'Edit Personality' : 'New Personality'}</LocalText>
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}
        <TextField
          hiddenLabel size="small" fullWidth placeholder="Name" label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={loading}
          sx={textFieldSx}
        />
        <TextField
          select hiddenLabel size="small" fullWidth
          data-testid="personality-form-sport-select"
          value={sports.length ? sportId : ''}
          onChange={(e) => setSportId(e.target.value)}
          disabled={loading}
          sx={{ ...textFieldSx, mt: 1.5 }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value=""><em><LocalText>Sport (optional)</LocalText></em></MenuItem>
          {sports.map((s) => (
            <MenuItem key={s.id} value={s.id}>{s.Name}</MenuItem>
          ))}
        </TextField>
        <Box sx={{ mt: 1.5 }}>
          <DropdownTextField
            label="Bio (optional)"
            values={bio}
            attribute="value"
            onChange={setBio}
            disabled={loading}
          />
        </Box>
        <Box sx={{ mt: 1.5 }}>
          <DropdownTextField
            label="Photo URL (optional)"
            values={photo}
            attribute="mediaLink"
            onChange={setPhoto}
            previewable
            mediaType="image"
            urlValidationType="image"
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50', gap: 1.5 }}>
        <Button onClick={onClose} sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
          <LocalText>Cancel</LocalText>
        </Button>
        <Button
          variant="contained"
          disabled={!isValid || saving || loading}
          onClick={handleSubmit}
          sx={{
            bgcolor: '#0a326d', color: 'white', fontWeight: 700, textTransform: 'none',
            borderRadius: '10px', px: 3,
            '&:hover': { bgcolor: '#0d3d85' },
            '&.Mui-disabled': { bgcolor: '#c8d8ea', color: 'white' },
          }}
        >
          <LocalText>{saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}</LocalText>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PersonalityFormModal;
